import {AbstractControl, FormArray, FormControl, FormControlOptions, FormGroup} from '@angular/forms';

export function toFormControlArray<T>(list: T[], options: FormControlOptions = {}): FormControl<T | null>[] {
  return list.map(value => new FormControl<T | null>(value, options));
}

/**
 * Recursively collects validation errors from an AbstractControl and its descendants.
 *
 * @param {AbstractControl} control The control to collect errors from. This can be an instance of FormGroup, FormArray, or FormControl.
 * @param {string} [path=''] The path of the current control in the form hierarchy. Defaults to an empty string, which is used for the root control.
 * @return {Record<string, any>} A record object where keys represent the path of the controls with validation errors, and values contain the corresponding error objects.
 */
export function collectErrors(control: AbstractControl, path: string = ''): Record<string, any> {
  let errors: Record<string, any> = {};

  // Add current control errors
  if (control.errors) {
    errors[path || 'root'] = control.errors;
  }

  // Handle FormGroup
  if (control instanceof FormGroup) {
    Object.keys(control.controls).forEach(key => {
      const child = control.get(key);
      if (!child) return;

      const childPath = path ? `${path}.${key}` : key;
      Object.assign(errors, collectErrors(child, childPath));
    });
  }

  // Handle FormArray
  if (control instanceof FormArray) {
    control.controls.forEach((child, index) => {
      const childPath = `${path}[${index}]`;
      Object.assign(errors, collectErrors(child, childPath));
    });
  }

  return errors;
}
